import React, { useState, useRef } from 'react';
import LocationList from '../../LocationList';
import ModeHelpTooltip from './components/ModeHelpTooltip';
import SetupActions from './components/SetupActions';

import { importLocationsFromCsv } from '../../../Services/DataServices/DataIOService';
import { isSetupMode } from '../../../Services';
import { useTp } from '../../../context/TpContext';

import '../../styles/modes/setupMode.css';

export default function SetupMode() {
  const {
    locations, setLocations,
    setStartId, setPath, currentModeRef,

    addPointType, toggleAddPointType,
    defaultQuantity, setDefaultQuantity,
  } = useTp();

  const [showHelp, setShowHelp] = useState(false);
  const toggleHelp = (e) => { e.preventDefault(); setShowHelp(v => !v); };

  // Tabs: 'map' | 'manual' | 'load'
  const [activeTab, setActiveTab] = useState('map');


  // CSV upload states
  const [importError, setImportError] = useState('');
  const [importWarnings, setImportWarnings] = useState(null);
  const fileInputRef = useRef(null);

  // Quick Paste states
  const [quickText, setQuickText] = useState('');
  const [quickError, setQuickError] = useState('');
  const [quickLoading, setQuickLoading] = useState(false);

  // --- CSV Upload ---
  const handleImportClick = () => {
    setImportError('');
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError('');
    setImportWarnings(null);
    
    try {
      const result = await importLocationsFromCsv(file);
      
      // Handle new return format with validation information
      if (result.locations) {
        setLocations(result.locations);
        
        // Show validation warnings if any entries were skipped
        if (result.skippedEntries && result.skippedEntries.length > 0) {
          setImportWarnings({
            skipped: result.skippedEntries,
            total: result.totalProcessed,
            successful: result.successfullyImported
          });
        }
      } else {
        // Handle legacy return format (just array)
        setLocations(result);
      }
      
      setStartId('');
      e.target.value = '';
    } catch (error) {
      console.error('Failed to import location points:', error);
      setImportError(error.message || 'Failed to import location points');
      e.target.value = '';
    }
  };

  // --- Quick Paste (CSV text → import) ---
  const handleQuickImport = async () => {
    setQuickError('');
    if (!quickText.trim()) {
      setQuickError('Please paste CSV text first.');
      return;
    }
    setQuickLoading(true);
    try {
      const lines = quickText
        .split(/\r?\n/)
        .map(l => l.trim())
        .filter(Boolean)
        .filter(l => /^(\d+|number)\s*,/i.test(l)); // keep header/rows

      if (!lines.length) throw new Error('No CSV rows detected');

      const hasHeader = /^number\s*,\s*type\s*,\s*quantity\s*,\s*address\s*,\s*latitude\s*,\s*longitude/i.test(lines[0]);
      const csvText = hasHeader
        ? lines.join('\n')
        : `Number,Type,Quantity,Address,Latitude,Longitude\n${lines.join('\n')}`;

      const file = new File([csvText], 'pasted.csv', { type: 'text/csv' });
      const imported = await importLocationsFromCsv(file);

      setLocations(imported);
      setStartId('');
    } catch (err) {
      setQuickError(err?.message || 'Failed to import pasted CSV');
    } finally {
      setQuickLoading(false);
    }
  };


  return (
    <>
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Set Path Points</h3>
          <button className="mode-help-button" onClick={toggleHelp} title="Help">?</button>
        </div>
        <p>Please add the location points to visit and set the starting point.</p>
      </div>

      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}

      {/* Row: Map Point Marking + Manual Input */}
      <div className="submode-row">
        <button
          type="button"
          className={`submode-btn ${activeTab === 'map' ? 'active' : ''}`}
          onClick={() => setActiveTab('map')}
          aria-pressed={activeTab === 'map'}
        >
          <span className="label">Map Point Marking</span>
        </button>

        <button
          type="button"
          className={`submode-btn ${activeTab === 'manual' ? 'active' : ''}`}
          onClick={() => setActiveTab('manual')}
          aria-pressed={activeTab === 'manual'}
        >
          <span className="label">Manual Input</span>
        </button>
      </div>

      {/* Full-width green: Load Points */}
      <button
        type="button"
        className={`submode-btn accent-green wide ${activeTab === 'load' ? 'active' : ''}`}
        onClick={() => setActiveTab('load')}
        aria-pressed={activeTab === 'load'}
      >
        <span className="label">Load Points</span>
      </button>

      {/* Shared content area under the triggers */}
      <div className="submode-content">
        {activeTab === 'map' && (
          <div className="tp-controls">
            <button
              type="button"
              className={`btn toggle ${addPointType === 'supply' ? 'supply' : ''}`}
              onClick={toggleAddPointType}
              title="Toggle between adding Supply and Demand points"
            >
              Add: {addPointType === 'supply' ? 'Supply' : 'Demand'}
              <span className="badge">{addPointType === 'supply' ? 'S' : 'D'}</span>
            </button>

            <input
              className="qty-input"
              type="number"
              min={0}
              step={1}
              value={defaultQuantity}
              onChange={(e) => setDefaultQuantity(Number(e.target.value))}
              placeholder="Quantity"
              title="Default quantity for newly added points"
              style={{ maxWidth: 140 }}
            />
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              Tip: Click on the map to add the selected type with this default quantity.
            </span>
          </div>
        )}

        {activeTab === 'manual' && (
          <div className="quick-paste-section">
            <h4 style={{ marginBottom: '0.5rem' }}>Paste CSV rows</h4>
            <textarea
              rows={6}
              value={quickText}
              onChange={(e) => setQuickText(e.target.value)}
              placeholder={
`Paste rows like these (one per line):
1,Supply,200,500 Loring St,-37.833,144.915
2,Demand,50,100 Collins St,-37.815,144.966`
              }
              style={{
                width: '100%',
                resize: 'vertical',
                padding: '0.5rem',
                borderRadius: 6,
                border: '1px solid #ced4da',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
              }}
            />
            <div className="upload-buttons" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="csv-upload-btn"
                onClick={handleQuickImport}
                disabled={quickLoading || !quickText.trim()}
                title="Import from pasted CSV text"
              >
                {quickLoading ? 'Adding…' : 'Add from Text'}
              </button>
              <button
                type="button"
                className="download-example-btn"
                onClick={() => setQuickText('')}
              >
                Clear
              </button>
            </div>
            {quickError && <div className="error-message" style={{ marginTop: 8 }}>Paste Error: {quickError}</div>}
          </div>
        )}

        {activeTab === 'load' && (
          <div className="csv-upload-section">
            <div className="upload-instructions">
              <h4>CSV File Format</h4>
              <code>Number,Type,Quantity,Address,Latitude,Longitude</code>
              <p>Example:</p>
              <pre>
{`1,Supply,200,500 Loring St,-37.833,144.915
2,Demand,50,100 Collins St,-37.815,144.966
3,Demand,60,200 Bourke St,-37.816,144.963`}
              </pre>
            </div>

            <div className="upload-buttons">
              <button type="button" className="csv-upload-btn" onClick={handleImportClick}>
                Upload CSV File
              </button>
              <a href="/tp-example.csv" download="tp-example.csv" className="download-example-btn">
                Download Example CSV
              </a>
            </div>

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".csv,text/csv"
              onChange={handleFileChange}
            />

            {importError && <div className="error-message" style={{ marginTop: 8 }}>Import Error: {importError}</div>}
            
            {/* Validation warnings for skipped entries */}
            {importWarnings && (
              <div style={{
                marginTop: '10px', 
                padding: '12px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeaa7', 
                borderRadius: '5px'
              }}>
                <h6 style={{color: '#856404', margin: '0 0 8px 0'}}>
                  ⚠️ Import Warnings: {importWarnings.skipped.length} entries skipped
                </h6>
                <div style={{fontSize: '0.9em', marginBottom: '8px'}}>
                  Successfully imported: <strong>{importWarnings.successful}</strong> out of <strong>{importWarnings.total}</strong> entries
                </div>
                
                <details style={{fontSize: '0.85em'}}>
                  <summary style={{cursor: 'pointer', fontWeight: 'bold', color: '#856404'}}>
                    View skipped entries ({importWarnings.skipped.length})
                  </summary>
                  <div style={{marginTop: '8px', maxHeight: '200px', overflowY: 'auto'}}>
                    {importWarnings.skipped.map((entry, idx) => (
                      <div key={idx} style={{
                        marginBottom: '6px', 
                        padding: '6px', 
                        backgroundColor: 'rgba(255,255,255,0.7)', 
                        borderRadius: '3px',
                        border: '1px solid #ffdddd'
                      }}>
                        <div style={{fontWeight: 'bold', color: '#d32f2f'}}>
                          Line {entry.lineNumber}: {entry.number} ({entry.type})
                        </div>
                        <div style={{color: '#666', fontSize: '0.9em'}}>
                          Error: {entry.error}
                        </div>
                        <div style={{color: '#999', fontSize: '0.8em', fontFamily: 'monospace'}}>
                          Raw: {entry.rawLine}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
                
                <div style={{marginTop: '8px', fontSize: '0.8em', color: '#666'}}>
                  <strong>Quantity Requirements:</strong> Must be positive whole numbers (integers).
                  <br />
                  <strong>Valid Examples:</strong> ✅ 10, 50, 100 | <strong>Invalid:</strong> ❌ -5, 0, 10.5, abc
                </div>
              </div>
            )}
          </div>
        )}
      </div>


      {/* Points list */}
      <LocationList
        locations={locations}
        mode="Setup Points"
        readOnly={false}
        showActions={true}
        allowDelete={true}
        onLocationChange={(next) => {
          setLocations(next);
        }}
      />

      {/* Reset */}
      <SetupActions
        locations={locations}
        setLocations={(next) => {
          setLocations(next);
        }}
        setStartId={setStartId}
        setPath={setPath}
        currentModeRef={currentModeRef}
        isSetupMode={isSetupMode}
      />
    </>
  );
}
